/**
 * Thread — message list in the design's walnut/gold treatment. The reducer
 * in App.tsx owns the thread; this component is pure render-from-props. The
 * sources are rendered as cards via SourceCard.
 *
 * No useState, no useEffect — auto-scroll on new messages uses a single
 * sanctioned useLayoutEffect (external system: the scroll container).
 */
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { AssistantMessage, ChatMessage } from "../state/chat-reducer";
import type { AnswerCitation } from "../api/answer-client";
import type { Language, Strings } from "../strings";
import { SourceCard } from "./SourceCard";
import { RelatedPassages } from "./RelatedPassages";
import { ReadingLink, RefusalHelp } from "./RefusalHelp";
import {
  citedChapterKeys,
  scopedRelatedChapterKeys,
  pickRelatedForThread,
  type RelatedFile,
  type RelatedPassage,
} from "../chat/related-passages";
import { BowSigil } from "./Wordmark";
import { AnswerMarkdown } from "./AnswerMarkdown";
import { copyText } from "../utils/copy-text";
import { buildShareUrl, shareQuestionForTurn } from "../share/share-query";
import { citeAnchorId, sourcesWrapId } from "../utils/citation-anchors";
import { FeedbackControls } from "./FeedbackControls";
import { ACKNOWLEDGE_ROUTE, CLARIFY_ROUTE } from "../chat/acknowledgement";
import { answerRefuses } from "../chat/refusal-help";
import { readFeedbackUrl } from "../chat/answer-config";

// Task 245: at most 3 follow-ups — enough to offer a path, few enough to
// stay a suggestion rather than a menu.
const RELATED_LIMIT = 3;

export interface ThreadProps {
  strings: Strings;
  thread: ChatMessage[];
  pendingQuery: string | null;
  pendingLabel: string;
  /** The morph direction that opened this thread (for scene transition CSS). */
  sceneDirection: "bowstring" | "aimed-shot" | null;
  /** Citation-chip click (Task 144): (messageId, 1-based n, the cited source). */
  onCite?: (messageId: string, n: number, citation: AnswerCitation) => void;
  /** Task 170: "usar mis palabras exactas" — re-ask the original verbatim. */
  onUseExactWords?: (originalQuery: string) => void;
  /** Task 245: affirmed-neighbour map; null until loaded / on failure. */
  related?: RelatedFile | null;
  /** Task 245: a follow-up chip submits a normal question. */
  onAskRelated?: (question: string) => void;
  /** Task 251 Layer 2: the chat language, sent with reader feedback. */
  language?: Language;
}

/**
 * Which treatment to show while the assistant turn is open but no token has
 * arrived yet (Task 137/139). "skeleton" = shimmering placeholder lines (warm
 * gold sweep over walnut — a lead-in to Task 141's "light condensing into text").
 * "embers" = three breathing gold points (the previous treatment, kept as a
 * one-line fallback). Flip this constant to switch.
 */
const THINKING_STYLE: "skeleton" | "embers" = "skeleton";

/** Embers thinking indicator (previous treatment; kept switchable). */
function Loader(props: { label: string }): JSX.Element {
  return (
    <div className="loader" role="status" aria-live="polite">
      <span className="loader__embers" aria-hidden="true">
        <span className="loader__ember" />
        <span className="loader__ember" />
        <span className="loader__ember" />
      </span>
      <span className="loader__label">{props.label}</span>
    </div>
  );
}

/** Skeleton thinking indicator: warm shimmer over placeholder answer lines. */
function AnswerSkeleton(props: { label: string }): JSX.Element {
  return (
    <div className="answer-skeleton" role="status" aria-live="polite" aria-label={props.label}>
      <span className="answer-skeleton__line" />
      <span className="answer-skeleton__line" />
      <span className="answer-skeleton__line" />
      <span className="answer-skeleton__line answer-skeleton__line--short" />
    </div>
  );
}

/** The active thinking indicator, selected by THINKING_STYLE. */
function ThinkingIndicator(props: { label: string }): JSX.Element {
  return THINKING_STYLE === "skeleton" ? (
    <AnswerSkeleton label={props.label} />
  ) : (
    <Loader label={props.label} />
  );
}

type CopyStatus = "idle" | "copied" | "failed";

function CopyGlyph(props: { copied: boolean }): JSX.Element {
  if (props.copied) {
    return (
      <svg className="answer__copy-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M5 13 L10 18 L19 7"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return (
    <svg className="answer__copy-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M5 15 V6 a2 2 0 0 1 2-2 h9"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Task 192: copy a share URL (/chat?q=<standalone question>) to the clipboard.
 * The URL carries ONLY the question — no answer, history, citations, or metadata. */
function ShareButton(props: { question: string; strings: Strings }): JSX.Element {
  const { question, strings } = props;
  const [status, setStatus] = useState<CopyStatus>("idle");
  useEffect(() => {
    if (status === "idle") return;
    const id = window.setTimeout(() => setStatus("idle"), 2000);
    return () => window.clearTimeout(id);
  }, [status]);
  function handleShare(): void {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    void copyText(buildShareUrl(origin, question)).then((ok) => setStatus(ok ? "copied" : "failed"));
  }
  const label = status === "copied" ? strings.shareCopied : status === "failed" ? strings.copyFailed : strings.shareLabel;
  return (
    <button
      type="button"
      className={`answer__copy answer__share${status === "idle" ? "" : ` answer__copy--${status}`}`}
      onClick={handleShare}
      data-testid="answer-share"
    >
      <svg className="answer__copy-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M8.6 13.5l6.8 3.5M15.4 7L8.6 10.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        <circle cx="17.5" cy="6" r="2.4" stroke="currentColor" strokeWidth="1.7" />
        <circle cx="6" cy="12" r="2.4" stroke="currentColor" strokeWidth="1.7" />
        <circle cx="17.5" cy="18" r="2.4" stroke="currentColor" strokeWidth="1.7" />
      </svg>
      <span className="answer__copy-label" aria-live="polite">{label}</span>
    </button>
  );
}

function CopyButton(props: { text: string; strings: Strings }): JSX.Element {
  const { text, strings } = props;
  const [status, setStatus] = useState<CopyStatus>("idle");

  // external: window.setTimeout — auto-reset the transient copy status back to
  // idle ~2s after an attempt. Cleanup: clearTimeout on status change/unmount.
  useEffect(() => {
    if (status === "idle") return;
    const id = window.setTimeout(() => setStatus("idle"), 2000);
    return () => window.clearTimeout(id);
  }, [status]);

  function handleCopy(): void {
    void copyText(text).then((ok) => setStatus(ok ? "copied" : "failed"));
  }

  const label =
    status === "copied"
      ? strings.copyDone
      : status === "failed"
        ? strings.copyFailed
        : strings.copy;

  return (
    <button
      type="button"
      className={`answer__copy${status === "idle" ? "" : ` answer__copy--${status}`}`}
      onClick={handleCopy}
    >
      <CopyGlyph copied={status === "copied"} />
      <span className="answer__copy-label" aria-live="polite">
        {label}
      </span>
    </button>
  );
}

export function Thread(props: ThreadProps): JSX.Element {
  const { strings, thread, pendingQuery, pendingLabel, sceneDirection, onCite, onUseExactWords, related, onAskRelated, language } = props;
  const scrollRef = useRef<HTMLDivElement | null>(null);

  /*
    Task 245: follow-ups are chosen for the CONVERSATION, not for one turn —
    pickRelatedForThread drops anything an earlier turn already offered (see its
    doc for the live case that forced this). Computed once here rather than
    inside each AssistantTurn, since a turn cannot see its predecessors.

    Memoised on a signature of the SETTLED citations, not on `thread`, so the
    arrays keep their identity while an answer streams — AssistantTurn is
    memo'd on props, and a fresh array each token would re-render every earlier
    turn in the thread. A streaming turn has `response === null`, so its slot
    stays empty until it settles, and the signature changes only then.
  */
  const assistants = thread.filter((m): m is AssistantMessage => m.role === "assistant");
  const citedPerTurn = assistants.map((m) => scopedRelatedChapterKeys(m.text,
    m.response?.sources ?? [], m.response?.route.references ?? []));
  const signature = assistants.map((m, i) => `${m.id}:${citedPerTurn[i]?.join(",") ?? ""}`).join("|");
  const relatedByMessage = useMemo(() => {
    const picks = pickRelatedForThread(citedPerTurn, related ?? null, RELATED_LIMIT);
    const byId = new Map<string, RelatedPassage[]>();
    assistants.forEach((m, i) => {
      const allCited = new Set(citedChapterKeys(m.response?.sources ?? []));
      byId.set(m.id, (picks[i] ?? []).filter(p => !allCited.has(p.key)));
    });
    return byId;
    // citedPerTurn/assistants are re-derived every render; `signature` is their
    // stable identity. Adding them here would defeat the memo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, related]);

  // external: HTMLElement scrollTop — pin the thread to the bottom when a
  // pending question/loader is added. Once the non-streaming answer arrives,
  // keep the viewport at the start of that answer instead of jumping to the
  // sources at the bottom.
  useLayoutEffect(() => {
    if (pendingQuery === null) return;
    const node = scrollRef.current;
    if (node === null) return;
    node.scrollTop = node.scrollHeight;
  }, [pendingQuery]);

  return (
    <div
      className="thread-scene"
      data-direction={sceneDirection ?? "bowstring"}
    >
      <div className="thread" ref={scrollRef}>
        <ol className="thread__inner" aria-live="polite">
          {thread.map((message, index) => {
            const next = thread[index + 1];
            if (message.role === "user") {
              const aiMessage = next?.role === "assistant" ? next : null;
              return (
                <li key={message.id} className="turn">
                  <div className="msg-user">{message.text}</div>
                  {aiMessage !== null ? (
                    <AssistantTurn
                      message={aiMessage}
                      strings={strings}
                      language={language ?? "es"}
                      originalQuery={message.text}
                      {...(onCite ? { onCite } : {})}
                      {...(onUseExactWords ? { onUseExactWords } : {})}
                      relatedPassages={relatedByMessage.get(aiMessage.id) ?? []}
                      {...(onAskRelated ? { onAskRelated } : {})}
                    />
                  ) : null}
                </li>
              );
            }
            // Assistant-only renders are handled with the preceding user msg.
            return null;
          })}
          {pendingQuery !== null ? (
            <li className="turn">
              <div className="msg-user">{pendingQuery}</div>
              <div className="msg-assistant">
                <BowSigil className="msg-assistant__sigil" />
                <Loader label={pendingLabel} />
              </div>
            </li>
          ) : null}
        </ol>
      </div>
    </div>
  );
}

/**
 * How many trailing tokens (words + whitespace, ~half are words) stay wrapped in
 * animatable spans. Older streamed text collapses into one plain text node, so
 * the animated span count — and React reconciliation per token — stays bounded
 * no matter how long the answer gets (Task 141 "cap animation work").
 */
const REVEAL_WINDOW_TOKENS = 80;

/**
 * Streaming answer body with the Task 141 "light condensation" reveal. The
 * growing text is split into word + whitespace tokens; the recent window renders
 * as spans keyed by ABSOLUTE token index — so already-revealed words keep their
 * identity and never reanimate, only freshly-arrived words animate (CSS runs
 * once on mount). Older tokens collapse into a single plain text node. The raw
 * text is unchanged, so copy-to-clipboard stays verbatim. Reduced-motion is
 * handled in CSS (a calm opacity fade instead of blur/transform).
 */
function RevealedText(props: { text: string }): JSX.Element {
  const { older, window } = useMemo(() => {
    const tokens = props.text === "" ? [] : props.text.split(/(\s+)/).filter((t) => t !== "");
    const cut = Math.max(0, tokens.length - REVEAL_WINDOW_TOKENS);
    return {
      older: tokens.slice(0, cut).join(""),
      window: tokens.slice(cut).map((t, k) => ({ t, ws: /^\s+$/.test(t), key: cut + k })),
    };
  }, [props.text]);
  return (
    <p className="answer__text" data-testid="answer-text">
      {older}
      {window.map((w) =>
        w.ws ? (
          <span key={w.key} className="reveal-ws">
            {w.t}
          </span>
        ) : (
          <span key={w.key} className="reveal-word">
            {w.t}
          </span>
        ),
      )}
      <span className="answer__cursor" aria-hidden="true" />
    </p>
  );
}

const AssistantTurn = memo(function AssistantTurn(props: {
  message: AssistantMessage;
  strings: Strings;
  language: Language;
  originalQuery?: string;
  onCite?: (messageId: string, n: number, citation: AnswerCitation) => void;
  onUseExactWords?: (originalQuery: string) => void;
  /** Already selected for the whole conversation by Thread. */
  relatedPassages?: RelatedPassage[];
  onAskRelated?: (question: string) => void;
}): JSX.Element {
  const { message, strings, originalQuery, onUseExactWords, relatedPassages, onAskRelated } = props;
  const { response } = message;
  // Task 170: the server rewrote an elliptical follow-up into a standalone
  // question. Show it transparently (we never replace the user's typed text) and
  // offer a one-click "use my exact words" re-ask.
  const rewrite = response?.rewrite;
  const streaming = message.status === "streaming";
  const errored = message.status === "error";
  // User-halted turn (Task: stop control). Settled, partial text kept, no error
  // chrome — rendered like a complete answer (Markdown) but with a neutral note
  // and no meta/sources (response is null).
  const stopped = message.status === "stopped";
  // Before the first token, the turn is open but empty: show a "thinking" loader
  // (Task 137). Real providers can take several seconds to their first content
  // token; the chat opens immediately and shows progress here rather than
  // freezing the idle scene. Once a token arrives we swap to the growing text.
  const thinking = streaming && message.text.length === 0;
  return (
    <div className="msg-assistant">
      <BowSigil className="msg-assistant__sigil" />
      <div className={`answer${streaming ? " answer--streaming" : " fade-in"}`}>
        {rewrite?.applied ? (
          <div className="answer__rewrite" role="note">
            <span className="answer__rewrite-text">
              {strings.rewriteInterpretedAs(rewrite.standalone_query)}
            </span>
            {onUseExactWords && typeof originalQuery === "string" ? (
              <button
                type="button"
                className="answer__rewrite-undo"
                onClick={() => onUseExactWords(originalQuery)}
              >
                {strings.rewriteUseExactWords}
              </button>
            ) : (
              <span className="answer__rewrite-hint">{strings.rewriteNotIt}</span>
            )}
          </div>
        ) : null}
        {thinking ? (
          <div data-testid="answer-thinking">
            <ThinkingIndicator label={message.rechecking ? strings.threadRecheckingLabel : strings.threadStreamingLabel} />
            {/* The skeleton's label is for screen readers only; a re-check can take
                several seconds, so say it on screen too (the skeleton announces it). */}
            {message.rechecking ? (
              <p className="answer__status" aria-hidden="true" data-testid="answer-rechecking">
                {strings.threadRecheckingLabel}
              </p>
            ) : null}
          </div>
        ) : streaming ? (
          <>
            {/*
              While streaming, words condense into view from warm light (Task
              141) on the same growing plain-text surface (Task 137). Markdown is
              deferred to settle (below) so we never re-parse mid-stream and the
              reveal stays word-level.
            */}
            <RevealedText text={message.text} />
            <p className="answer__status" role="status" aria-live="polite">
              {strings.threadStreamingLabel}
            </p>
          </>
        ) : (
          /* Settled (complete or error): safe Markdown (Task 140) + [n] citation
             chips linked to this answer's source cards (Task 144). */
          <AnswerMarkdown
            text={message.text}
            messageId={message.id}
            sources={message.response?.sources ?? []}
            {...(props.onCite ? { onCite: props.onCite } : {})}
          />
        )}
        {errored ? (
          <aside className="answer__incomplete" role="status">
            {strings.answerIncomplete}
          </aside>
        ) : null}
        {stopped ? (
          <aside className="answer__incomplete answer__stopped" role="status">
            {strings.answerStopped}
          </aside>
        ) : null}
        {/*
          Meta + sources appear only AFTER answer.final settles `response`
          (Task 137): sources are not known mid-stream. The desktop SourceRail
          (Task 067) mirrors this — it withholds the current answer's sources
          until the turn completes.
        */}
        {response !== null ? (
          <>
            <div className="answer__meta">
              {message.createdAt !== undefined ? (
                <time className="answer__time" dateTime={new Date(message.createdAt).toISOString()}>
                  {strings.threadTimestamp(message.createdAt)}
                </time>
              ) : null}
              {/* A courtesy reply or a request for the whole question is not
                  worth copying or sharing. */}
              {response.route.type === ACKNOWLEDGE_ROUTE || response.route.type === CLARIFY_ROUTE ? null : (
                <CopyButton text={message.text} strings={strings} />
              )}
              {response.route.type === ACKNOWLEDGE_ROUTE || response.route.type === CLARIFY_ROUTE ? null : (() => {
                // Task 192: share the RESOLVED standalone (rewrite) or the
                // original user text — never the answer. Hidden when neither yields
                // a question (e.g. no original query available).
                const q = shareQuestionForTurn({
                  userText: originalQuery ?? "",
                  standaloneQuery: rewrite?.standalone_query,
                });
                return q !== null ? <ShareButton question={q} strings={strings} /> : null;
              })()}
              {/* Task 251 Layer 2: one reader rating per settled answer, tied to
                  the proxy's request id (absent on the direct-RAG dev path). */}
              {message.status === "complete" && typeof response.request_id === "string" ? (
                <FeedbackControls
                  requestId={response.request_id}
                  language={props.language}
                  strings={strings}
                  endpoint={readFeedbackUrl()}
                  question={originalQuery ?? ""}
                  answer={message.text}
                  sources={response.sources}
                />
              ) : null}
            </div>
            {/*
              Task 245: follow-ups BEFORE the sources — sources are for
              verifying the answer you just read; these are for going somewhere
              next. Every chip rides a connection Koresh affirms, and they
              SUBMIT rather than navigate, so they are safe in embed mode.
              A refusal has no sources and no follow-ups: it gets a next step
              instead (RefusalHelp), read from the question as the server
              understood it. So does an answer that ends in the refusal sentence
              after some reasoning (answerRefuses). An answer to a request to
              read a book gets one line into the library (ReadingLink).
            */}
            {response.route.type === ACKNOWLEDGE_ROUTE || response.route.type === CLARIFY_ROUTE ? null : (response.sources.length === 0 || answerRefuses(message.text)) && message.status === "complete" ? (
              <RefusalHelp
                query={rewrite?.applied ? rewrite.standalone_query : originalQuery ?? ""}
                language={props.language}
                strings={strings}
                {...(onAskRelated ? { onAsk: onAskRelated } : {})}
              />
            ) : (
              <>
                {message.status === "complete" ? (
                  <ReadingLink
                    query={rewrite?.applied ? rewrite.standalone_query : originalQuery ?? ""}
                    strings={strings}
                  />
                ) : null}
                {onAskRelated !== undefined ? (
                  <RelatedPassages
                    strings={strings}
                    passages={relatedPassages ?? []}
                    onPick={onAskRelated}
                  />
                ) : null}
              </>
            )}
            {/*
              Inline per-answer sources. Mobile-only: hidden on desktop via CSS,
              where the right SourceRail (Task 067) shows the latest answer's
              sources instead. Rendered as a native <details> so the collapse
              needs no JS state — a compact single-column disclosure rather than
              the cramped two-column grid the desktop rail uses.
            */}
            <div className="inline-sources-wrap" id={sourcesWrapId(message.id)}>
              {response.sources.length === 0 ? null : (
                <details className="inline-sources">
                  <summary className="inline-sources__summary">
                    {strings.inlineSourcesToggle(response.sources.length)}
                  </summary>
                  <ul className="inline-sources__list">
                    {response.sources.map((citation, idx) => (
                      // id is the citation-chip scroll target (Task 144); 1-based n.
                      <li key={`${citation.path}-${idx}`} id={citeAnchorId(message.id, idx + 1)}>
                        <SourceCard citation={citation} strings={strings} />
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
});
